import React from 'react';
import { TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';

const CASES = [
  {
    title: 'The \u00A315,000 Dilapidations Discovery',
    problem: 'A landlord\'s lease contained vague dilapidations wording with no schedule of condition. The tenant was facing a \u00A327,000 terminal dilapidations claim.',
    solution: 'Our AI identified the ambiguous clause and recommended a schedule of condition be annexed. The rewritten clause limited the tenant\'s obligation to the condition at lease commencement.',
    impact: '\u00A312,000+ reduction in dilapidations exposure',
    tag: 'Estimated savings',
  },
  {
    title: 'Rent Review Clause Pitfall',
    problem: 'A commercial lease included an upward-only rent review with the landlord\'s surveyor as sole arbiter, effectively giving the landlord unchecked power over rent increases.',
    solution: 'Our review flagged the one-sided mechanism and provided a rewritten clause with independent RICS surveyor determination and market evidence requirements.',
    impact: 'Protected against \u00A380,000+ in potential overpayment',
    tag: 'Protected exposure',
  },
  {
    title: 'LTA 1954 Contracting Out Issue',
    problem: 'The landlord attempted to contract out of the Landlord & Tenant Act 1954 but failed to follow the statutory declaration procedure correctly.',
    solution: 'Our AI identified the procedural deficiency and provided compliant contracting-out documentation, ensuring the landlord could recover possession at lease expiry.',
    impact: 'Secured legal certainty on termination and renewal',
    tag: 'Legal certainty',
  },
];

export function CommercialCaseStudies() {
  return (
    <section className="py-20 sm:py-28 bg-gray-50 bg-diagonal-lines">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Real-World Results</h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            How landlords and property managers protected themselves with our reviews
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {CASES.map((c, i) => (
            <div
              key={c.title}
              className={`animate-on-scroll delay-${(i + 1) * 100} bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-elevated transition-shadow duration-300`}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-semibold text-gray-900 text-sm leading-snug pr-2">{c.title}</h3>
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">{c.tag}</span>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">The Problem</span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{c.problem}</p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">The Solution</span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{c.solution}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 bg-gray-50 px-6 py-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-semibold text-gray-900">{c.impact}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
